import React, { useState, useRef, useCallback } from "react";
import { Save, Star, Upload, X } from "lucide-react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TestimonialForm = ({
  testimonialData,
  setTestimonialData,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const isSubmitting = useRef(false);
  const saveTimeoutRef = useRef(null);  // ✅ Add debounce timeout

  const cardCls = "bg-white rounded-xl shadow p-8 mb-10";
  const labelCls = "block text-sm font-medium text-gray-700 mb-1";
  const inputCls = "w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTestimonialData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setTestimonialData((prev) => ({ ...prev, rating }));
  };

  // ============================= 
  // Image Upload
  // ============================= 
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type.toLowerCase())) {
      alert("Please upload JPG or PNG only.");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("media", file);

    try {
      setIsUploading(true);
      
      const res = await axios.post(
        `${API_BASE_URL}/admin/upload-media`,
        uploadData,
        {
          headers: { 
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`
          },
        }
      );

      const url = res?.data?.data?.files?.[0]?.url;
      if (url) {
        setTestimonialData((prev) => ({ ...prev, image: url }));
        alert("✅ Image uploaded successfully!");
      } else {
        alert("Image upload failed. Please try again.");
      }
    } catch (error) {
      console.error("Image upload error:", error);
      alert(`Failed to upload image: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    setTestimonialData((prev) => ({ ...prev, image: "" }));
  };

  const isFormValid = () => {
    return (
      testimonialData.name?.trim() &&
      testimonialData.time?.trim() &&
      testimonialData.rating > 0 &&
      testimonialData.text?.trim()
    );
  };

  // ✅ Memoized save handler with comprehensive protection
  const handleSave = useCallback(async () => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    console.log("🔵 Save clicked at", new Date().toISOString());
    console.log("Current state:", { isSaving, isSubmitting: isSubmitting.current });

    // ✅ Triple lock: state + ref + timeout
    if (isSaving || isSubmitting.current) {
      console.log("⚠️ Already saving, blocked!");
      return;
    }

    if (!isFormValid()) {
      console.log("❌ Form validation failed");
      alert("Please fill all required fields");
      return;
    }

    try {
      // Lock everything
      isSubmitting.current = true;
      setIsSaving(true);

      console.log("📤 Calling parent onSave...");
      
      // Call parent save
      await onSave();
      
      console.log("✅ Save completed successfully");
      
    } catch (error) {
      console.error("❌ Save failed:", error);
      alert("Failed to save testimonial. Please try again.");
    } finally {
      // Delayed unlock to prevent rapid double-clicks
      saveTimeoutRef.current = setTimeout(() => {
        setIsSaving(false);
        isSubmitting.current = false;
        console.log("🔓 Save unlocked");
      }, 1000);  // ✅ 1 second delay before allowing next save
    }
  }, [isSaving, onSave, testimonialData, isFormValid]);

  // ✅ Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <section className={cardCls}>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Testimonial Details
        </h2>
        <button
          type="button"
          onClick={handleSave}
          disabled={!isFormValid() || isSaving || isUploading}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
            isFormValid() && !isSaving && !isUploading
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-60"
          }`}
        >
          <Save size={18} /> 
          {isSaving ? "Saving..." : "Save"}
        </button>
      </div>

      {/* name + time */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className={labelCls}>
            Customer Name <span className="text-red-500">*</span>
          </label>
          <input
            name="name"
            value={testimonialData.name || ""}
            onChange={handleChange}
            disabled={isSaving || isUploading}
            className={inputCls}
            placeholder="e.g., Praveen Kumar"
            required
          />
        </div>
        <div>
          <label className={labelCls}>
            Time <span className="text-red-500">*</span>
          </label>
          <input
            name="time"
            value={testimonialData.time || ""}
            onChange={handleChange}
            disabled={isSaving || isUploading}
            className={inputCls}
            placeholder="e.g., a year ago"
            required
          />
        </div>
      </div>

      {/* rating */}
      <div className="mb-6">
        <label className={labelCls}>
          Rating <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => handleRatingClick(star)}
              disabled={isSaving || isUploading}
              className="focus:outline-none transition-all disabled:opacity-50"
            >
              <Star
                size={32}
                className={`${
                  star <= (testimonialData.rating || 0)
                    ? "fill-yellow-400 text-yellow-400"
                    : "fill-gray-200 text-gray-300"
                } hover:fill-yellow-300 hover:text-yellow-300`}
              />
            </button>
          ))}
          <span className="ml-3 text-gray-600 self-center">
            {(testimonialData.rating || 0)} / 5
          </span>
        </div>
      </div>

      {/* text */}
      <div className="mb-6">
        <label className={labelCls}>
          Testimonial Text <span className="text-red-500">*</span>
        </label>
        <textarea
          name="text"
          value={testimonialData.text || ""}
          onChange={handleChange}
          disabled={isSaving || isUploading}
          className={inputCls}
          placeholder="Enter customer testimonial..."
          rows={6}
          required
        />
      </div>

      {/* Image Upload */}
      <div>
        <label className={labelCls}>
          Customer Image (Optional)
        </label>
        
        {testimonialData.image ? (
          <div className="relative inline-block">
            <img
              src={testimonialData.image}
              alt="Customer"
              className="w-32 h-32 rounded-lg object-cover border-2 border-gray-300"
            />
            <button
              type="button"
              onClick={removeImage}
              disabled={isSaving || isUploading}
              className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center bg-gray-50 hover:bg-gray-100 transition-colors">
            <input
              type="file"
              id="testimonial-image-upload"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleImageUpload}
              disabled={isSaving || isUploading}
              className="hidden"
            />
            <label
              htmlFor="testimonial-image-upload"
              className={`flex flex-col items-center ${
                isSaving || isUploading ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              }`}
            >
              <Upload size={40} className="text-gray-400 mb-3" />
              <span className="text-gray-700 font-medium mb-1">
                {isUploading ? "Uploading..." : "Click to upload image"}
              </span>
              <span className="text-sm text-gray-500">
                JPG or PNG • Max 300 KB
              </span>
              <span className="text-xs text-gray-400 mt-1">
                Recommended: 1:1 ratio (square)
              </span>
            </label>
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialForm;